import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Trash2, AlertTriangle, LogOut, Shield, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountSettingsPage() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm.');
      return;
    }

    setDeleting(true);
    try {
      await api.delete('/auth/delete-account');
      toast.success('Your account has been deleted.');
      logout();
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid="settings-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="text-lg font-bold text-slate-900 font-fredoka">Account Settings</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-4">
        {/* Account Info */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" data-testid="account-info-card">
          <h2 className="text-base font-bold text-slate-900 font-fredoka mb-4">Account Information</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Display Name</span>
              <span className="font-semibold text-slate-800">{user?.display_name || user?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Age Group</span>
              <span className="font-semibold text-slate-800">{user?.age ? `${user.age} years old` : 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Member Since</span>
              <span className="font-semibold text-slate-800">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" data-testid="legal-links-card">
          <h2 className="text-base font-bold text-slate-900 font-fredoka mb-4">Legal</h2>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/privacy-policy')}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
              data-testid="privacy-policy-link"
            >
              <Shield className="w-5 h-5 text-blue-500" />
              <span className="text-sm font-medium text-slate-700">Privacy Policy</span>
            </button>
            <button
              onClick={() => navigate('/terms')}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
              data-testid="terms-link"
            >
              <FileText className="w-5 h-5 text-blue-500" />
              <span className="text-sm font-medium text-slate-700">Terms of Service</span>
            </button>
          </div>
        </div>

        {/* Logout */}
        <button
          onClick={() => { logout(); navigate('/login'); }}
          className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors"
          data-testid="settings-logout-btn"
        >
          <LogOut className="w-5 h-5 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Log Out</span>
        </button>

        {/* Danger Zone */}
        <div className="bg-white rounded-2xl shadow-sm border-2 border-red-200 p-6" data-testid="danger-zone-card">
          <h2 className="text-base font-bold text-red-600 font-fredoka mb-2">Danger Zone</h2>
          <p className="text-sm text-slate-600 mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
            All quiz history, coins, pets, and progress will be lost.
          </p>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl text-sm font-semibold hover:bg-red-100 transition-colors"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4" />
              Delete My Account
            </button>
          ) : (
            <div className="space-y-3 p-4 bg-red-50 rounded-xl" data-testid="delete-confirm-section">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                <p className="text-sm text-red-700 font-medium">
                  This will permanently delete all your data. Type <strong>DELETE</strong> to confirm.
                </p>
              </div>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                data-testid="delete-confirm-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting || confirmText !== 'DELETE'}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="delete-confirm-btn"
                >
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
                <button
                  onClick={() => { setShowDeleteConfirm(false); setConfirmText(''); }}
                  className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-300 transition-colors"
                  data-testid="delete-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

      </main>
    </div>
  );
}
